import { useEffect, useRef, useState } from "preact/hooks";
import { errorMessage, joinRoom } from "../../api/client";
import { chooseLoadout, gamePaused, matchActive, nextLoadout, pauseView, pointerLocked, resumeGame } from "../../game/pause";
import { lobbyView } from "../../game/lobby";
import { me } from "../../state/auth";
import { gameError, gameJoin, gameLoadProgress, gameStatus, leaveGame, startGame } from "../../state/game";
import { RoomKind } from "../../api/types";
import { mapName, modeName, weaponName } from "../../state/meta";
import { navigate, screen } from "../../state/route";
import { Button } from "../components/Button";
import { DisplayPlate } from "../components/DisplayPlate";
import { OptionCard } from "../components/OptionCard";
import { Progress } from "../components/Progress";
import { Spinner } from "../components/Spinner";
import { useEscape } from "../hooks/hooks";
import { useArrowNav } from "../hooks/useArrowNav";
import { LobbyPanel } from "./Lobby";

const STEPS = ["Connect", "Load map", "Spawn"] as const;

/**
 * In-match container. While the session connects or loads it shows the "Connecting to <room>"
 * overlay with a Leave button, on failure an error panel, and while playing nothing at all (the
 * canvas and the HUD own the screen). `gameStatus` is driven by the game session.
 */
export function Game() {
  const status = gameStatus.value;
  const join = gameJoin.value;
  const error = gameError.value;
  const [retrying, setRetrying] = useState(false);

  // Escape leaves while the overlay is up. While playing the game session owns Escape.
  useEscape(leaveGame, status !== "playing");

  if (status === "playing" || !join) return null;

  const retry = async () => {
    setRetrying(true);
    try {
      // A fresh single-use ticket for the same room (also the reconnect path of docs/NETCODE.md).
      const fresh = await joinRoom(join.room.id, join.password);
      if (gameJoin.value === join) startGame(fresh, join.password); // not when the player left meanwhile
    } catch (e) {
      if (gameJoin.value === join) gameError.value = errorMessage(e);
    } finally {
      setRetrying(false);
    }
  };

  const room = join.room;

  if (status === "error") {
    return (
      <section class="gate gate--error screen-enter" role="alert" aria-labelledby="gate-title">
        <DisplayPlate kicker="Connection failed" titleId="gate-title">
          Couldn't join {room.name}
        </DisplayPlate>
        <div class="gate__body">
          <div class="notice notice--error gate__error">{error || "The connection was closed."}</div>
          <div class="gate__actions">
            <Button variant="primary" size="lg" busy={retrying} onClick={() => void retry()}>
              Retry
            </Button>
            <Button size="lg" onClick={leaveGame}>
              Leave
            </Button>
          </div>
        </div>
      </section>
    );
  }

  const step = status === "connecting" ? 0 : 1;
  return (
    <section class="gate screen-enter" role="status" aria-live="polite" aria-labelledby="gate-title">
      <DisplayPlate kicker={status === "connecting" ? "Opening connection" : "Loading map"} titleId="gate-title">
        Connecting to {room.name}
      </DisplayPlate>
      <div class="gate__body">
        <p class="gate__meta">
          {modeName(room.mode)} · {mapName(room.map)} · {room.players}/{room.maxPlayers} players
        </p>
        {room.kind === RoomKind.RANKED && (
          <p class="gate__meta">Ranked: leaving this match counts as a loss.</p>
        )}
        <ol class="gate__steps" aria-label="Progress">
          {STEPS.map((label, i) => (
            <li key={label} class={i < step ? "is-done" : i === step ? "is-active" : undefined}>
              <span class="gate__step-dot" aria-hidden="true" />
              {label}
            </li>
          ))}
        </ol>
        {status === "loading" && <Progress value={Math.round(gameLoadProgress.value * 100)} max={100} label="Loading map" />}
        <div class="gate__actions">
          <Spinner size={22} />
          <Button size="lg" onClick={leaveGame}>
            Leave
          </Button>
        </div>
      </div>
    </section>
  );
}

type PauseView = "menu" | "loadout";

/**
 * In-match overlay, mounted once by the game session controller (the menu shell renders nothing
 * while a match is playing): the pause menu, or a "click to play" hint while the mouse is free.
 * It hides while another screen (the settings opened from the pause menu) is on top.
 */
export function GameOverlay() {
  if (!matchActive.value || screen.value !== "game") return null;
  if (gamePaused.value) return pauseView.value === "lobby" && lobbyView.value !== null ? <LobbyPanel /> : <PauseMenu />;
  if (!pointerLocked.value) {
    return (
      <div class="play-hint" role="status">
        Click to play
      </div>
    );
  }
  return null;
}

function PauseMenu() {
  const [view, setView] = useState<PauseView>("menu");
  const [confirmLeave, setConfirmLeave] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  useArrowNav(panel);

  useEffect(() => {
    // Escape steps back inside the menu, or resumes (the match owns Escape while it is playing).
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.isComposing) return;
      e.preventDefault();
      if (view === "loadout") setView("menu");
      else resumeGame();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view]);

  useEffect(() => {
    panel.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
  }, [view]);

  const room = gameJoin.value?.room;
  const ranked = room?.kind === RoomKind.RANKED;
  return (
    <div class="pause" role="dialog" aria-modal="true" aria-labelledby="pause-title">
      <div class="pause__panel" ref={panel}>
        <DisplayPlate kicker={room ? room.name : "Match"} titleId="pause-title">
          {view === "menu" ? "Paused" : "Loadout"}
        </DisplayPlate>
        <div class="pause__body">
          {view === "menu" ? (
            <>
              <p class="pause__note">The match keeps running while you are in this menu.</p>
              {ranked && <p class="pause__note">Ranked: leaving this match counts as a loss.</p>}
              <div class="pause__actions">
                <Button variant="primary" size="lg" full data-autofocus="" onClick={resumeGame}>
                  Resume
                </Button>
                <Button size="lg" full onClick={() => setView("loadout")}>
                  Loadout
                </Button>
                {lobbyView.value !== null && (
                  <Button size="lg" full onClick={() => (pauseView.value = "lobby")}>
                    Lobby
                  </Button>
                )}
                <Button size="lg" full onClick={() => navigate("settings")}>
                  Settings
                </Button>
                <Button
                  variant="danger"
                  size="lg"
                  full
                  onClick={ranked && !confirmLeave ? () => setConfirmLeave(true) : leaveGame}
                >
                  {ranked && confirmLeave ? "Really leave? Counts as a loss" : "Leave match"}
                </Button>
              </div>
            </>
          ) : (
            <LoadoutPicker onBack={() => setView("menu")} />
          )}
        </div>
      </div>
    </div>
  );
}

function LoadoutPicker({ onBack }: { onBack: () => void }) {
  const account = me.value;
  const loadouts = account?.loadouts ?? [];
  const current = nextLoadout.value ?? account?.selectedLoadout ?? 0;
  return (
    <>
      <p class="pause__note">Applies the next time you spawn.</p>
      <div class="pause__loadouts" role="radiogroup" aria-label="Loadout for the next spawn">
        {loadouts.map((l, i) => (
          <OptionCard key={i} role="radio" selected={i === current} onClick={() => chooseLoadout(i)}>
            <span class="card__title">{l.name || `Loadout ${i + 1}`}</span>
            <span class="card__meta">
              {weaponName(l.primary.weapon)} · {weaponName(l.secondary.weapon)}
            </span>
          </OptionCard>
        ))}
      </div>
      <div class="pause__actions">
        <Button size="lg" full data-autofocus="" onClick={onBack}>
          Back
        </Button>
      </div>
    </>
  );
}
